import styled from 'styled-components';
import { devices } from 'shared/styles/components';

export const LandingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 48px 24px;
  min-height: calc(100vh - 128px);

  @media ${devices.laptopSMax} {
    min-height: auto;
  }
`;

export const LandingBody = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
`;

export const LandingColumns = styled.div`
  display: flex;
  flex: 1;
  gap: 80px;
  align-items: stretch;

  &.ltr {
    flex-direction: row;
  }
  &.rtl {
    flex-direction: row-reverse;
  }

  @media ${devices.laptopSMax} {
    &.ltr {
      flex-direction: column;
    }
    &.rtl {
      flex-direction: column-reverse;
    }
  }

  @media ${devices.mobileMax} {
    &.ltr {
      flex-direction: column;
    }
    &.rtl {
      flex-direction: column-reverse;
    }
  }
`;

export const LandingColumn = styled.div`
  user-select: none;

  &.left {
    flex: 0 0 auto;
    display: flex;
    align-items: center;

    @media ${devices.mobileMax} {
      width: 100%;
      justify-content: center;
    }

    .image-container {
      position: relative;
      display: flex;
      align-items: flex-end;
      flex-direction: column;
      gap: 12px;
      max-width: 580px;

      &.composite {
        position: relative;
      }

      .main-image-link {
        display: inline-block;
        position: relative;
        z-index: 2;
        overflow: hidden;
        aspect-ratio: 580 / 357;
        width: min(580px, 100%);
        background: ${({ theme }) => theme.colors.bgPopup};
        border-radius: 8px;
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        cursor: pointer;

        &:hover {
          transform: scale(1.02);
        }
      }

      .overlay-image {
        position: absolute;
        z-index: 3;
        overflow: hidden;
        background: ${({ theme }) => theme.colors.bgPopup};
        border-radius: 8px;
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        cursor: pointer;

        &:hover {
          transform: scale(1.05);
          z-index: 5;
        }

        &.glaci {
          bottom: -30px;
          left: -30px;
          aspect-ratio: 151 / 267;
          width: 151px;

          @media ${devices.mobileMax} {
            bottom: -24px;
            left: -5px;
            width: 80px;

            img {
              max-width: 80px !important;
              height: auto !important;
            }
          }
        }

        &.nexum {
          bottom: -30px;
          right: -30px;
          aspect-ratio: 300 / 162;
          width: 300px;

          @media ${devices.mobileMax} {
            bottom: -8px;
            right: -5px;
            width: 100px;

            img {
              max-width: 100px !important;
              height: auto !important;
            }
          }
        }

        img {
          box-shadow: 0 8px 15px rgba(0, 0, 0, 0.3);
        }
      }

      img {
        position: relative;
        z-index: 2;
        box-shadow: 0 8px 15px rgba(0, 0, 0, 0.2);

        &.poweron-pro {
          max-width: 100%;
          height: auto;
        }
        &.symx {
          margin-right: 32px;
          margin-bottom: 140px;
          max-width: 95%;
          height: 100%;

          @media ${devices.mobileMax} {
            margin-right: 32px;
            margin-bottom: 70px;
          }
        }
        &.naas {
          position: absolute;
          right: 0;
          bottom: -16px;
          max-width: 65%;
          height: fit-content;

          @media ${devices.mobileMax} {
            right: -24px;
          }
        }
      }
      .dot-mask {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 1;
        height: calc(100% + 2 * 72px);
        width: min(calc(100% + 192px), 100vw);
        background-image: radial-gradient(${({ theme }) => theme.colors.radialColor} 1px, transparent 1px);
        background-size: 16px 16px;
        -webkit-mask-image: radial-gradient(ellipse 50% 50% at 50% 50%, #000 90%, transparent 100%);
        mask-image: radial-gradient(ellipse 50% 50% at 50% 50%, #000 90%, transparent 100%);
      }

      @media ${devices.mobileMax} {
        align-items: center;
        max-width: calc(100vw - 48px);

        .glaci-toggle {
          display: none;
        }
      }
    }
  }

  &.right {
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: flex-start;
    gap: 40px;
    max-width: 550px;
    min-height: 494px;
    z-index: 2;

    &.nexum-api {
      @media ${devices.miniMobileMax} {
        min-height: 550px;
      }
    }

    @media ${devices.laptopSMax} {
      min-height: 480px;
    }

    @media ${devices.mobileMax} {
      padding-bottom: 48px;
    }
  }
`;

export const LandingRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;

  h1 {
    margin-bottom: 12px;
    color: ${({ theme }) => theme.colors.textPrimary};
    font-size: 52px;
    font-family: ${({ theme }) => theme.fontFamily.display};
    font-weight: 600;
    user-select: none;

    .accent {
      color: ${({ theme }) => theme.colors.textTertiary};
    }

    .typing-line {
      display: block;
      overflow: hidden;
    }

    .typing-content {
      display: block;
      padding-bottom: 0.15em;
    }

    .typing-text {
      display: inline;
      color: ${({ theme }) => theme.colors.textTertiary};
      word-break: break-word;
      overflow-wrap: break-word;
    }

    @media ${devices.tabletMax} {
      font-size: 40px;
    }

    @media ${devices.mobileMax} {
      font-size: 32px;
    }
  }

  p {
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 22px;
    line-height: 1.5;
    user-select: none;
  }

  .landing-button {
    display: flex;
    align-items: center;
    gap: 4px;
    transition: transform 0.3s ease;

    &.install {
      background-color: ${({ theme }) => theme.colors.success};
    }

    &.request,
    &.join {
      gap: 4px;
    }

    &.request svg {
      width: 16px;
      height: 16px;
    }

    &:hover {
      transform: scale(1.1);
    }

    svg {
      fill: ${({ theme }) => theme.colors.white};
      width: 16px;
      height: 16px;
    }
  }
`;

export const LandingAnnouncement = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 24px;
  text-align: left;

  a {
    color: ${({ theme }) => theme.colors.textSecondary};
    line-height: 1;

    &:hover {
      color: ${({ theme }) => theme.colors.textTertiary};
      text-decoration: none;
    }
  }

  .tag {
    margin-top: 2px;
    padding: 0 5px;
    letter-spacing: 0.5px;
    line-height: 1.78;
    vertical-align: middle;
    text-transform: uppercase;
    font-weight: 500;
    font-size: 9px;
    border-radius: 4px;

    &.new {
      color: ${({ theme }) => theme.colors.textTertiary};
      border: 2px solid ${({ theme }) => theme.colors.textTertiary};
    }

    &.discover {
      color: ${({ theme }) => theme.colors.success};
      border: 2px solid ${({ theme }) => theme.colors.success};
    }

    &.learn {
      color: ${({ theme }) => theme.colors.warning};
      border: 2px solid ${({ theme }) => theme.colors.warning};
    }
  }
`;

export const LandingActions = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;

  a {
    margin: 0;
    color: ${({ theme }) => theme.colors.textPrimary};
    font-weight: 600;
    letter-spacing: 0;
    line-height: 19px;
    text-decoration: none;
    transition: color 0.2s;

    &:hover {
      color: ${({ theme }) => theme.colors.textTertiary};
      text-decoration: none;
    }
  }
`;

export const LandingLogos = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 4px;
  max-width: 90vw;
  user-select: none;

  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.gray600};
    opacity: 0.75;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;

    span {
      text-transform: lowercase;
    }
  }

  .logo-wall {
    display: flex;
    gap: 16px;
    align-items: center;
    justify-content: flex-start;

    @media ${devices.mobileMax} {
      gap: 20px;
    }
  }

  .logo-slot {
    flex: 0 0 80px;
    width: 80px;
    height: 40px;
    overflow: hidden;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;

    @media ${devices.mobileMax} {
      flex: 0 0 60px;
      width: 60px;
      height: 30px;
    }
  }

  .logo-item {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    opacity: 1;
    transform: translateY(0);
    transition:
      opacity 0.4s ease-out,
      transform 0.4s ease-out;

    svg {
      width: auto;
      max-width: 100%;
      max-height: 100%;
      fill: ${({ theme }) => theme.colors.gray600};
      opacity: 0.75;

      .max_cu_svg__transparent {
        fill: ${({ theme }) => theme.colors.bgPrimary};
      }

      @media ${devices.mobileMax} {
        max-height: 100%;
      }
    }

    &.entering {
      opacity: 0;
      transform: translateY(20px);
      animation: logoSlideIn 0.4s ease-out forwards;
    }

    &.exiting {
      opacity: 0;
      transform: translateY(-20px);
    }
  }

  @keyframes logoSlideIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
